import { E_AGGREGATION_TYPE, IAggregationData, IAggregationItem, IAggregationParams, IQuery } from '@patsnap/synapse_common_interface'
import { useLocale } from '@pharmsnap/shared/src/composition'
import { sharedCtx } from '@pharmsnap/shared/src/context'
import { getYearRange } from '@pharmsnap/shared/src/utils'
import { computed, reactive, ref, shallowRef, unref, watch } from '@vue/composition-api'
import { MaybeRef } from '@vueuse/core'
import { cloneDeep, isUndefined } from 'lodash'
import { IAnalysisChartControlItem, IAnalysisChartControlLayoutItem, IAnalysisDisplayIconType } from '../../BAnalysisChartNext/type'

export type IUseLiteratureAggChartType = 'year-line' | 'drug-bar' | 'target-bar' | 'disease-bar' | 'org-bar'

/**
 * 趋势图时间范围，数字表示最近 N 年
 */
export type IUseLiteratureTrendRangeValue = 'all' | 5 | 10 | 20

type LiteratureAggChartModel = {
  display: IAnalysisDisplayIconType
  rangeType?: { selectedValue: IUseLiteratureTrendRangeValue }
  top?: { selectedValue: number }
  rollup?: boolean
}

export interface IUseLiteratureAggChartOptions {
  query?: MaybeRef<IQuery>
  type: MaybeRef<IUseLiteratureAggChartType>
}

/**
 * 趋势分桶的起始年份，与专利分析的自定义时间下限保持一致
 */
const TREND_START_YEAR = 1780

const emptyAggData: IAggregationData = { aggregation_result: [{ items: [], aggregation_field: '', total: 0 }], total: 0 }

const aggregationItemRecordType: Record<IUseLiteratureAggChartType, IAggregationItem> = {
  /**
   * 趋势维度按年份直方图聚合，配合 extended_bounds 指定分桶区间，空年份会补齐为 0
   */
  'year-line': {
    aggregation_type: E_AGGREGATION_TYPE.DATE_HISTOGRAM_YEAR,
    aggregation_field: 'YEAR',
    limit: 100,
  },
  'drug-bar': {
    aggregation_type: E_AGGREGATION_TYPE.TERMS,
    aggregation_field: 'PHS_DRUG_ID',
  },
  'target-bar': {
    aggregation_type: E_AGGREGATION_TYPE.TERMS,
    aggregation_field: 'PHS_TARGET_ID',
  },
  'disease-bar': {
    aggregation_type: E_AGGREGATION_TYPE.TERMS,
    aggregation_field: 'PHS_DISEASE_ID',
  },
  /**
   * 机构维度与过滤面板保持一致，默认按总公司口径聚合，可由 rollup 开关切换为子公司口径；
   * 不提供 Top 切换，固定取前 10
   */
  'org-bar': {
    aggregation_type: E_AGGREGATION_TYPE.TERMS,
    aggregation_field: 'PHS_ORG_ROOT_ID_ONEID',
    limit: 10,
  },
}

export const lineChartTypes: IUseLiteratureAggChartType[] = ['year-line']

export const barChartTypes: IUseLiteratureAggChartType[] = ['drug-bar', 'target-bar', 'disease-bar', 'org-bar']

const supportRangeRadioTypes: IUseLiteratureAggChartType[] = ['year-line']

const supportTopControlChartTypes: IUseLiteratureAggChartType[] = ['drug-bar', 'target-bar', 'disease-bar']

const supportRollupControlChartTypes: IUseLiteratureAggChartType[] = ['org-bar']

export function useLiteratureAggChart(options: IUseLiteratureAggChartOptions) {
  const { query = { type: 'group', must: [], filter: [] }, type } = options

  const { locale } = useLocale()

  const loading = ref(true)

  const isEmpty = ref(true)

  const model = reactive(initModel(unref(type)))

  const controls = reactive(initControls(unref(type)))

  const controlLayout = reactive(initControlLayout(false))

  const controlLayoutFullscreen = reactive(initControlLayout(true))

  const originalAggData = shallowRef<IAggregationData>(cloneDeep(emptyAggData))

  const aggParams = computed(() => {
    const params: IAggregationParams = {
      data_type: 'paper',
      query: cloneDeep(unref(query)),
      aggregation: [getAggregationItem(unref(type))],
    }
    return params
  })

  /**
   * 聚合结果始终取全量，最近 N 年在前端按年份截取，避免切换时重新请求
   */
  const aggData = computed<IAggregationData>(() => {
    const sourceData = unref(originalAggData)
    const selectedRange = model.rangeType?.selectedValue

    if (isUndefined(selectedRange) || selectedRange === 'all') {
      return sourceData
    }

    const aggResult = sourceData.aggregation_result?.[0]
    const items = aggResult?.items

    if (!aggResult || !items?.length) {
      return sourceData
    }

    const years = items.map((item) => Number(item.key)).filter((year) => !Number.isNaN(year))

    if (!years.length) {
      return sourceData
    }

    const minVisibleYear = Math.max(...years) - selectedRange + 1

    return {
      ...sourceData,
      aggregation_result: [
        {
          ...aggResult,
          items: items.filter((item) => Number(item.key) >= minVisibleYear),
        },
      ],
    }
  })

  const categoryName = computed(() => {
    const isCn = unref(locale) === 'cn'

    switch (unref(type)) {
      case 'drug-bar':
        return isCn ? '药物' : 'Drug'
      case 'target-bar':
        return isCn ? '靶点' : 'Target'
      case 'disease-bar':
        return isCn ? '适应症' : 'Indication'
      case 'org-bar':
        return isCn ? '机构' : 'Organization'
      default:
        return isCn ? '发表年份' : 'Publication Year'
    }
  })

  const countName = computed(() => (unref(locale) === 'cn' ? '文献数量' : 'No. of Publications'))

  const settingDataPanelTitle = computed(() => {
    if (supportRangeRadioTypes.includes(unref(type))) {
      return unref(locale) === 'cn' ? '时间范围' : 'Time Range'
    }
    if (supportRollupControlChartTypes.includes(unref(type))) {
      return unref(locale) === 'cn' ? '机构' : 'Organization'
    }
    return ''
  })

  const settingChartPanelTitle = computed(() => {
    if (!supportTopControlChartTypes.includes(unref(type))) {
      return ''
    }

    const isCn = unref(locale) === 'cn'

    switch (unref(type)) {
      case 'drug-bar':
        return isCn ? '药物排名' : 'Drug Range'
      case 'target-bar':
        return isCn ? '靶点排名' : 'Target Range'
      case 'disease-bar':
        return isCn ? '适应症排名' : 'Indication Range'
      default:
        return ''
    }
  })

  watch(aggData, () => {
    const items = unref(aggData).aggregation_result?.[0]?.items
    isEmpty.value = !items || items.length === 0 || items.every((item) => item.count === 0)
  })

  /**
   * top 影响 limit、rollup 影响 aggregation_field，两者变更都必须重新请求；
   * display 与 rangeType 只影响前端呈现，不在此列
   */
  watch(
    () => [model.top?.selectedValue, model.rollup],
    () => fetchAggData()
  )

  async function fetchAggData() {
    loading.value = true
    const rt = await sharedCtx.service.literature.getAggregation(aggParams.value)
    if (rt.success) {
      const items = rt.data.aggregation_result?.[0]?.items
      const hasNoData = !items || items.length === 0 || items.every((item) => item.count === 0)
      originalAggData.value = hasNoData ? cloneDeep(emptyAggData) : rt.data
    } else {
      originalAggData.value = cloneDeep(emptyAggData)
    }
    loading.value = false
  }

  function getAggregationItem(chartType: IUseLiteratureAggChartType): IAggregationItem {
    const clonedAggregationItem = cloneDeep(aggregationItemRecordType[chartType])

    const { top, rollup } = model

    if (!isUndefined(top)) {
      clonedAggregationItem.limit = top.selectedValue
    }

    if (!isUndefined(rollup)) {
      clonedAggregationItem.aggregation_field = rollup ? 'PHS_ORG_ROOT_ID_ONEID' : 'PHS_ORG_ID_ONEID'
    }

    /**
     * date_histogram_year 需要显式的分桶区间，否则后端无法划分年份
     */
    if (clonedAggregationItem.aggregation_type === E_AGGREGATION_TYPE.DATE_HISTOGRAM_YEAR) {
      const { from, to } = getYearRange(TREND_START_YEAR, new Date().getFullYear())
      clonedAggregationItem.extended_bounds_from = from
      clonedAggregationItem.extended_bounds_to = to
    }

    return clonedAggregationItem
  }

  function initModel(chartType: IUseLiteratureAggChartType) {
    const initialModel: LiteratureAggChartModel = {
      display: barChartTypes.includes(chartType) ? 'horizontal-bar' : 'single-line',
    }

    if (supportRangeRadioTypes.includes(chartType)) {
      initialModel.rangeType = { selectedValue: 20 }
    }

    if (supportTopControlChartTypes.includes(chartType)) {
      initialModel.top = { selectedValue: 10 }
    }

    if (supportRollupControlChartTypes.includes(chartType)) {
      initialModel.rollup = true
    }

    return initialModel
  }

  function initControls(chartType: IUseLiteratureAggChartType) {
    const controlItems: IAnalysisChartControlItem<keyof LiteratureAggChartModel>[] = []

    if (lineChartTypes.includes(chartType)) {
      controlItems.push({
        type: 'display',
        bindField: 'display',
        items: [
          { type: 'single-line', value: 'single-line' },
          { type: 'table', value: 'table' },
        ],
      })
    }

    if (barChartTypes.includes(chartType)) {
      controlItems.push({
        type: 'display',
        bindField: 'display',
        items: [
          { type: 'horizontal-bar', value: 'horizontal-bar' },
          { type: 'table', value: 'table' },
        ],
      })
    }

    if (supportRangeRadioTypes.includes(chartType)) {
      controlItems.push({
        type: 'radio',
        bindField: 'rangeType',
        items: [
          { type: 'common', value: 5, label: unref(locale) === 'cn' ? '近5年' : 'Last 5 Years' },
          { type: 'common', value: 10, label: unref(locale) === 'cn' ? '近10年' : 'Last 10 Years' },
          { type: 'common', value: 20, label: unref(locale) === 'cn' ? '近20年' : 'Last 20 Years' },
          { type: 'common', value: 'all', label: unref(locale) === 'cn' ? '全部' : 'All' },
        ],
      })
    }

    if (supportTopControlChartTypes.includes(chartType)) {
      controlItems.push({
        type: 'radio',
        bindField: 'top',
        items: [
          { label: unref(locale) === 'cn' ? '前10' : 'Top 10', type: 'common', value: 10 },
          { label: unref(locale) === 'cn' ? '前20' : 'Top 20', type: 'common', value: 20 },
        ],
      })
    }

    if (supportRollupControlChartTypes.includes(chartType)) {
      controlItems.push({
        type: 'rollup',
        bindField: 'rollup',
      })
    }

    return controlItems
  }

  function initControlLayout(isFullScreen = false) {
    const layoutItems: Array<IAnalysisChartControlLayoutItem<keyof LiteratureAggChartModel>> = [
      {
        field: 'display',
        position: 'left',
      },
      {
        field: 'rangeType',
        position: isFullScreen ? 'data-panel' : 'middle',
      },
      {
        field: 'top',
        position: isFullScreen ? 'chart-panel' : 'middle',
      },
      {
        field: 'rollup',
        position: isFullScreen ? 'data-panel' : 'middle',
      },
    ]

    return layoutItems
  }

  return {
    model,
    controls,
    controlLayout,
    controlLayoutFullscreen,
    aggData,
    categoryFieldName: categoryName,
    countFieldName: countName,
    fetchAggData,
    loading,
    isEmpty,
    settingDataPanelTitle,
    settingChartPanelTitle,
  }
}
